import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Layout from '../../layout/Main/Layout';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Avatar,
  CircularProgress
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { get, post, put, del, postFiles } from '../../config/axios';
import toast from 'react-hot-toast';

const Teams = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentMember, setCurrentMember] = useState(null);
  const [newMember, setNewMember] = useState({
    name: '',
    title: '',
    description: '',
    imageUrl: '',
    quote: '',
    highlights: []
  });
  const [imageFile, setImageFile] = useState(null);
  const fileInputRef = useRef(null);

  // Quill editor modules
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  };

  // Quill editor formats
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link'
  ];

  // Fetch all team members
  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await get('/api/dashboard/website/teams');
      setTeamMembers(response);
    } catch (error) {
      toast.error('Failed to fetch team members');
      console.error('Error fetching team members:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  // Handle image upload
  const handleImageUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await postFiles("/api/app/user/uploadImage", formData);
      return response.data.url;
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
      return null;
    }
  };

  const handleOpenAddDialog = () => {
    setCurrentMember(null);
    setNewMember({
      name: '',
      title: '',
      description: '',
      imageUrl: '',
      quote: '',
      highlights: []
    });
    setImageFile(null);
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (member) => {
    setCurrentMember(member);
    setNewMember({
      ...member,
      quote: member.quote || '',
      highlights: member.highlights || []
    });
    setImageFile(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewMember(prev => ({ ...prev, [name]: value }));
  };

  const handleDescriptionChange = (value) => {
    setNewMember(prev => ({ ...prev, description: value }));
  };

  const handleHighlightChange = (index, value) => {
    const updatedHighlights = [...newMember.highlights];
    updatedHighlights[index] = value;
    setNewMember(prev => ({ ...prev, highlights: updatedHighlights }));
  };

  const addHighlightField = () => {
    setNewMember(prev => ({
      ...prev,
      highlights: [...prev.highlights, '']
    }));
  };

  const removeHighlightField = (index) => {
    const updatedHighlights = [...newMember.highlights];
    updatedHighlights.splice(index, 1);
    setNewMember(prev => ({ ...prev, highlights: updatedHighlights }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match('image.*')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      setNewMember(prev => ({ ...prev, imageUrl: event.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSaveMember = async () => {
    try {
      if (!newMember.name || !newMember.title) {
        toast.error('Name and title are required');
        return;
      }

      let imageUrl = newMember.imageUrl;

      if (imageFile) {
        const uploadedUrl = await handleImageUpload(imageFile);
        if (!uploadedUrl) return;
        imageUrl = uploadedUrl;
      }

      const memberData = {
        name: newMember.name,
        title: newMember.title,
        description: newMember.description,
        imageUrl,
        quote: newMember.quote,
        highlights: newMember.highlights.filter(h => h.trim() !== '')
      };

      if (currentMember) {
        const response = await put(`/api/dashboard/website/teams/${currentMember._id}`, memberData);
        setTeamMembers(teamMembers?.map(member =>
          member._id === currentMember._id ? response : member
        ));
        toast.success('Team member updated successfully');
      } else {
        const response = await post('/api/dashboard/website/teams', memberData);
        setTeamMembers([...teamMembers, response]);
        toast.success('Team member added successfully');
      }
      setOpenDialog(false);
      fetchTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
      console.error('Error saving team member:', error);
    }
  };

  const handleDeleteMember = async (id) => {
    if (window.confirm('Are you sure you want to delete this team member?')) {
      try {
        await del(`/api/dashboard/website/teams/${id}`);
        setTeamMembers(teamMembers.filter(member => member._id !== id));
        toast.success('Team member deleted successfully');
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to delete team member');
        console.error('Error deleting team member:', error);
      }
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    // Remove HTML tags for the preview
    const plainText = text.replace(/<[^>]*>/g, '');
    if (plainText.length <= maxLength) return plainText;
    return `${plainText.substring(0, maxLength)}...`;
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ padding: '0 !important' }}>
        <Box sx={{ my: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Manage Team Members
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenAddDialog}
            >
              Add Team Member
            </Button>
          </Box>

          {teamMembers?.length === 0 ? (
            <Typography variant="body1" sx={{ textAlign: 'center', mt: 30 }}>
              No team members found. Add your first team member.
            </Typography>
          ) : (
            <Grid container spacing={5}>
              {teamMembers?.map((member) => (
                <Grid item xs={12} sm={6} md={4} key={member._id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardMedia
                      component="img"
                      image={member.imageUrl}
                      alt={member.name}
                      sx={{ height: 350, objectFit: 'cover' }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography gutterBottom variant="h5" component="div">
                        {member.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {member.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {truncateText(member.description, 50)}
                      </Typography>
                    </CardContent>
                    <Box sx={{ px: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton
                        aria-label="edit"
                        onClick={() => handleOpenEditDialog(member)}
                        sx={{ mr: 1 }}
                      >
                        <Edit sx={{ color: "#4285F4" }} />
                      </IconButton>
                      <IconButton
                        aria-label="delete"
                        onClick={() => handleDeleteMember(member._id)}
                      >
                        <Delete sx={{ color: "red" }} />
                      </IconButton>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
          <DialogTitle>
            {currentMember ? 'Edit Team Member' : 'Add New Team Member'}
          </DialogTitle>
          <DialogContent>
            <Box
              component="form"
              sx={{
                '& .MuiTextField-root': { my: 1, width: '100%' },
                '& .ql-container': { height: '200px' }
              }}
              noValidate
              autoComplete="off"
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    name="name"
                    label="Full Name"
                    value={newMember.name}
                    onChange={handleInputChange}
                  />
                  <TextField
                    required
                    name="title"
                    label="Position/Title"
                    value={newMember.title}
                    onChange={handleInputChange}
                  />
                  <TextField
                    name="quote"
                    label="Quote"
                    value={newMember.quote}
                    onChange={handleInputChange}
                    multiline
                    fullWidth
                  />
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                    Professional Biography (Rich Text)
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={newMember.description}
                    onChange={handleDescriptionChange}
                    modules={modules}
                    formats={formats}
                    style={{ height: '200px', marginBottom: '40px' }}
                  />

                  <Box mt={10}>
                    <Typography variant="subtitle2" gutterBottom>
                      Profile Image
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                    >
                      {imageFile ? 'Change Profile Image' : 'Upload Profile Image'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleFileSelect}
                        ref={fileInputRef}
                      />
                    </Button>
                    {newMember.imageUrl && (
                      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Avatar
                          src={newMember.imageUrl}
                          alt="Profile Preview"
                          sx={{ width: 100, height: 100, mb: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {imageFile?.name || 'Current profile image'}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Key Highlights
                  </Typography>
                  {newMember.highlights?.map((highlight, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TextField
                        value={highlight}
                        onChange={(e) => handleHighlightChange(index, e.target.value)}
                        multiline
                        fullWidth
                      />
                      <IconButton
                        onClick={() => removeHighlightField(index)}
                        sx={{ ml: 1 }}
                      >
                        <Delete color="error" />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={addHighlightField}
                    sx={{ mt: 1 }}
                  >
                    Add Highlight
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSaveMember}
              variant="contained"
              disabled={!newMember.name || !newMember.title || (!newMember.imageUrl && !imageFile)}
            >
              {currentMember ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default Teams;